import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { ProductionSpeedOptionDto, StepEnum } from '@/api-sdk';
import { global, GlobalComponentType } from '@/context';
import { baseStyles, rootVariablesStyles, typographyStyles } from '@/styles';

import '@/components/configurator/production/fg-production-speed';
import '../fg-configurator-step';

@customElement('fg-configurator-step-production-speed')
export class FgConfigurator extends LitElement {
  /**
   * Array of possible production options in the configurator
   */
  @property({ type: Array })
  options!: Array<ProductionSpeedOptionDto>;

  @property({ type: String })
  stepType?: StepEnum;

  /**
   * Store the current selected production speed info to render the cutoff time info string that is rendered outside of
   * the options loop.
   */
  @state()
  selectedProductionSpeedInfo?: {
    cutoffTime: string;
    timeInDays: number;
  };

  @state()
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ) {
    if (_changedProperties.has('options')) {
      const selectedValue = this.options.findIndex(option => option.selected);
      const cutoffTime = this.options[selectedValue]?.cutoffTime;
      const { layout } = this.globalContext;
      const timeInDays =
        layout === 'retail'
          ? this.options[selectedValue]?.deliveryTimeInDays
          : this.options[selectedValue]?.shippingTimeInDays;

      if (!cutoffTime || !timeInDays) {
        this.selectedProductionSpeedInfo = undefined;
        return;
      }

      this.selectedProductionSpeedInfo = {
        cutoffTime: cutoffTime,
        timeInDays: timeInDays,
      };
    }
  }

  handleProductionSpeedSelect(queryString?: string | null) {
    if (!queryString) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString: queryString,
          },
        },
      }),
    );
  }

  render() {
    if (!this.options || this.options.length === 0) {
      return nothing;
    }
    const { layout } = this.globalContext;

    return html`
      <fg-configurator-step
        part=${layout}
        .stepType=${this.stepType}
        .stepTitle=${layout === 'retail'
          ? t('headers.chooseDeliverySpeed')
          : null}
      >
        ${layout === 'wholesale'
          ? html`
              <fg-step-header
                part=${layout}
                slot="step-header"
                .stepType=${StepEnum.production_speed}
              ></fg-step-header>
            `
          : nothing}
        <fg-production-speed
          part=${layout}
          .options=${this.options}
          showProductionSpeedStep
          @production-speed-selected=${(event: CustomEvent) => {
            if (event.detail.option.queryString) {
              this.handleProductionSpeedSelect(event.detail.option.queryString);
            }
          }}
        ></fg-production-speed>
      </fg-configurator-step>
    `;
  }
  static styles = [
    baseStyles,
    typographyStyles,
    rootVariablesStyles,
    css`
      fg-production-speed {
        display: block;
        margin-top: 1rem;
      }
    `,
  ];
}
